import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import get from 'just-safe-get';
import { paramTypes, derivedParamTypes } from './fieldTypes';
import ErrorMessage from './ErrorMessage';
import Tooltip from '@/components/core/Tooltip';
import GroupToggleField from './GroupToggleField';
import LocationScalingTable from './LocationScalingTable';
import usePatchParams from '../usePatchParams';
import {
  transformGroupdFieldValues,
  hasLocationScalingEnable,
  isLocationScalingEnabled,
  findLocationScalingSchema,
} from '@/utils/paramsUtils';
import { DISPLAY_TYPES } from '@/consts';

const GroupField = ({ paramSchema, param, locationScalingDisabled, parentName, onChange }) => {
  const {
    formState: { errors },
    control,
    resetField,
    getValues,
  } = useFormContext();
  const { name, params: nestedParamSchemas, label, tooltip, source, display_options: displayOptions } = paramSchema;
  const error = get(errors, name);
  const { patchGroup } = usePatchParams();
  const displayType = displayOptions?.[0] ?? DISPLAY_TYPES.row;
  const isCollapsed = displayOptions?.[1] === 'collapsed';
  const fullName = parentName ? `${parentName}.${name}` : name;
  const hasLocationScaling = hasLocationScalingEnable(param);
  const locationScalingEnabled = isLocationScalingEnabled(param);
  const isLocationScalingGroup = name === 'location_scaling';
  const useLocationScalingTable = isLocationScalingGroup && param && displayType === DISPLAY_TYPES.table;

  const handleOnChange = async () => {
    const values = getValues(name);
    const transformedValues = transformGroupdFieldValues(values, nestedParamSchemas);

    if (!transformedValues) return;

    try {
      await patchGroup(name, { [name]: { params: transformedValues } });
      resetField(name);
    } catch (error) {
      console.error(error);
    }
  };

  const changeHandler = onChange ?? handleOnChange;
  const errorMessage = error?.message?.params?.[0];
  const prefix = `${fullName}.`;

  const renderLabel = () => {
    if (isLocationScalingGroup || (!label && !tooltip) || locationScalingDisabled) return null;

    return (
      <>
        {label && <div className="block truncate text-xs font-semibold">{label}</div>}
        {tooltip && <Tooltip label={label} text={tooltip} source={source} />}
      </>
    );
  };

  const renderParams = () => {
    return nestedParamSchemas
      .filter(nestedParamSchema => nestedParamSchema.name !== 'location_scaling_enabled')
      .filter(
        nestedParamSchema => !(nestedParamSchema.name === 'base_value' && hasLocationScaling && locationScalingEnabled),
      )
      .map(nestedParamSchema => {
        const childName = `${prefix}${nestedParamSchema.name}`;
        const nestedParam = param?.params[nestedParamSchema.name];
        const { primary: { value } = {} } = nestedParam ?? {};
        const { derived } = value ?? {};
        const { type } = nestedParamSchema;
        const Component = derived ? derivedParamTypes[type] : paramTypes[type];

        if (type === 'group') {
          const isLocationScalingChild = nestedParamSchema.name === 'location_scaling';
          const hideForCollapse = isCollapsed && !(isLocationScalingChild && hasLocationScaling);
          return (
            <div className={`mt-4 relative ${hideForCollapse ? 'hidden' : ''}`} key={childName}>
              <GroupField
                parentName={fullName}
                onChange={changeHandler}
                locationScalingDisabled={hasLocationScaling && !locationScalingEnabled}
                paramSchema={nestedParamSchema}
                param={nestedParam}
              />
            </div>
          );
        }

        return Component && nestedParam ? (
          <Component
            key={childName}
            name={childName}
            onChange={changeHandler}
            paramSchema={nestedParamSchema}
            param={nestedParam}
            displayType={displayType}
            isGroupField={true}
          />
        ) : null;
      });
  };

  return (
    <>
      <div className="mb-2 pt-1 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 self-center">{renderLabel()}</div>
          {hasLocationScaling && (
            <GroupToggleField
              name={`${prefix}location_scaling_enabled`}
              paramSchema={findLocationScalingSchema(paramSchema)}
              onChange={handleOnChange}
              value={param?.params?.location_scaling_enabled?.primary?.value}
              readOnly={false}
            />
          )}
        </div>
      </div>
      {displayType === DISPLAY_TYPES.table ? (
        <div
          className={`border rounded ${
            (useLocationScalingTable ? locationScalingDisabled : isCollapsed || locationScalingDisabled) ? 'hidden' : ''
          }`}
        >
          {useLocationScalingTable ? (
            <LocationScalingTable
              fullName={fullName}
              param={param}
              paramSchema={paramSchema}
              onChange={changeHandler}
              label={label}
              tooltip={tooltip}
              source={source}
            />
          ) : (
            <table className="w-full">
              <thead className="text-xs border-b">
                <tr>
                  <td className="p-2 pl-4 w-1/4">Name</td>
                  <td className="p-2 w-2/5">Value</td>
                  <td className="p-2 pr-4 w-1/5">Unit</td>
                </tr>
              </thead>
              <tbody>
                <Controller control={control} name={fullName} render={() => renderParams()} />
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <Controller control={control} name={fullName} render={() => renderParams()} />
      )}
      <div className="flex justify-center">
        <ErrorMessage text={errorMessage} />
      </div>
    </>
  );
};

GroupField.propTypes = {
  paramSchema: PropTypes.object,
  param: PropTypes.object,
  locationScalingDisabled: PropTypes.bool,
  parentName: PropTypes.string,
  onChange: PropTypes.func,
};

export default GroupField;
